import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useZoomStore } from '@/shared/store/zoomStore';
import { AutosaveStatus } from './AutosaveStatus';
import { FormErrorSummary } from './FormErrorSummary';
import type { AutosaveState } from '@/shared/hooks/useAutosave';

// Pages render <PageActions>{their action buttons}</PageActions> anywhere
// in their JSX; the children are portaled into SubNav's right slot
// (#subnav-actions-slot, mounted by SubNav). While this component is
// mounted it sets the zoom store's `hasPageActions` flag so SubNav can
// hide the "Esc to go back" pill in favour of the actions.
//
// Pages own the children JSX (mutation state, click handlers) — only the
// rendered output is moved. An autosaving form passes `autosave` and its
// state shows left of the buttons.
export function PageActions({ children, autosave, error }: {
  children: ReactNode;
  autosave?: AutosaveState | null;
  // What stopped the last save: a focused alert where PageActions sits in the
  // page (the top of the form), not in the portaled slot.
  error?: string | string[] | null;
}) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const setHasPageActions = useZoomStore((s) => s.setHasPageActions);

  useEffect(() => {
    setTarget(document.getElementById('subnav-actions-slot'));
    setHasPageActions(true);
    return () => setHasPageActions(false);
  }, [setHasPageActions]);

  return (
    <>
      {target && createPortal(<>{autosave && <AutosaveStatus state={autosave} className="mr-1" />}{children}</>, target)}
      <FormErrorSummary error={error} className="mb-2" />
    </>
  );
}

// A thin divider inside a page's action group: keeps a destructive action
// (Delete, Void) apart from the primary one (Post), so one is not a slip away
// from the other (forms-18).
export function ActionDivider() {
  return <span aria-hidden="true" className="mx-1 h-4 w-px self-center bg-border" />;
}
